import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { Nullable } from "../../../../pojo/Util";
import { StateService } from "../../../../services/state.service";
import { TelemetryService } from "../../../../services/telemetry.service";
import { s25Resource } from "../../../../util/s25.resource";
import { S25Util } from "../../../../util/s25-util";
import { ReportDashboardComponent } from "../../../../standalone/report-dashboard/report.dashboard.component";
import { reportExportFileName } from "../../../../standalone/report-dashboard/report.export";
import { ReportDrillDownEvent } from "../../../../standalone/report-dashboard/report.types";
import { Budget } from "../Budget";
import { BudgetLabels } from "../budget.labels";
import { BudgetService } from "../budget.service";
import {
    BUDGET_REPORT_MEASURES,
    BUDGET_REPORT_WIDGETS,
    BUDGET_REPORTS_DASHBOARD_ID,
    budgetSummaryCards,
    DEFAULT_BUDGET_REPORT_MEASURE,
    drillDownColumns,
    drillDownTableRows,
    formatBudgetAmount,
    budgetReportValidationMessage,
    isBudgetReportMeasure,
    withPlanTypeDisplayLabels,
} from "./budget.reports.util";

const TELEMETRY_CATEGORY = "StudentEngagement";
const TELEMETRY_SUB_CATEGORY = "BudgetReports";

interface DrillDownSelection {
    widgetTitle: string;
    dimension: Budget.ReportDimension;
    rowKey: string;
    rowLabel: string;
    seriesKey?: string;
}

/** Initial query params: prefer the ui-router hash query (production), fall back to the raw search (tests). */
function readSeed(): URLSearchParams {
    const routerSearch = StateService.getSearch();
    if (Object.keys(routerSearch).length) {
        return new URLSearchParams(routerSearch);
    }
    return new URLSearchParams(window.location.search);
}

function parseId(raw: unknown): number | undefined {
    const parsed = S25Util.parseInt(raw as string);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function parseIdList(raw: string | null): number[] {
    if (!raw) return [];
    return raw
        .split(",")
        .map((token) => parseId(token.trim()))
        .filter((id): id is number => id !== undefined);
}

function idsOf(items: DropDownItem[]): number[] {
    return items.map((item) => parseId(item.itemId)).filter((id): id is number => id !== undefined);
}

function fiscalYearToItem(fiscalYear: Budget.FiscalYear): DropDownItem<number> {
    return { itemId: fiscalYear.fiscalYearId, itemName: fiscalYear.name, itemTypeId: Budget.BUDGET_ITEM_TYPE_ID };
}

/** Deep-linked year first, then the current fiscal year, then the first one returned. */
function defaultFiscalYearItem(
    fiscalYears: Budget.FiscalYear[] | undefined,
    seededFiscalYearId: number | undefined,
): DropDownItem<number> | null {
    if (!fiscalYears?.length) return null;
    const fiscalYear =
        fiscalYears.find((candidate) => candidate.fiscalYearId === seededFiscalYearId) ??
        fiscalYears.find((candidate) => candidate.isCurrent) ??
        fiscalYears[0];
    return fiscalYearToItem(fiscalYear);
}

@TypeManagerDecorator("s25-ng-budget-reports")
@Component({
    selector: "s25-ng-budget-reports",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25DropdownPaginatedModule,
        S25LoadingInlineModule,
        S25TableModule,
        ReportDashboardComponent,
    ],
    host: { class: "budget-reports" },
    template: `
        <h1 class="budget-reports-title">{{ labels.BUDGET_REPORTS }}</h1>

        @if (fiscalYearsRes.isLoading()) {
            <s25-ng-loading-inline-static [text]="'Loading fiscal years...'" />
        } @else if (!hasFiscalYears()) {
            <p class="budget-reports-empty">No fiscal years found. Add a fiscal year to see budget reports.</p>
        } @else {
            <s25-ng-report-dashboard
                [dashboardId]="dashboardId"
                [title]="labels.BUDGET_REPORTS"
                [widgets]="widgets"
                [breakdowns]="breakdowns()"
                [series]="series()"
                [measure]="measure()"
                [measureOptions]="measureOptions"
                [cards]="cards()"
                [loading]="reportRes.isLoading()"
                [error]="reportError()"
                [formatValue]="formatAmount"
                (drillDown)="onDrillDown($event)"
                (exportWidget)="onExportWidget()"
            >
                <div class="budget-reports-controls" report-filters>
                    <div class="budget-reports-field">
                        <label for="budget-reports-fiscal-year">Fiscal Year</label>
                        <s25-ng-dropdown-search-criteria
                            type="fiscalYears"
                            class="c-input budget-reports-fiscal-year"
                            [fieldId]="'budget-reports-fiscal-year'"
                            [(value)]="fiscalYearItem"
                            placeholder="Select fiscal year"
                            ariaLabel="Fiscal year"
                        />
                    </div>
                    <div class="budget-reports-field">
                        <label for="budget-reports-measure">Measure</label>
                        <select
                            id="budget-reports-measure"
                            class="c-input"
                            [ngModel]="measure()"
                            (ngModelChange)="onMeasureChange($event)"
                        >
                            @for (option of measureOptions; track option.key) {
                                <option [value]="option.key">{{ option.label }}</option>
                            }
                        </select>
                    </div>
                    <div class="budget-reports-field budget-reports-field--wide">
                        <span class="budget-reports-field-label" id="budget-reports-orgs-label">Organizations</span>
                        <s25-ng-dropdown-multi-search-criteria
                            [type]="'studentEngagementOrganizations'"
                            [value]="orgsModel()"
                            [hasDone]="false"
                            [placeholder]="'All organizations'"
                            [ariaLabel]="'Filter by organization'"
                            (done)="onOrganizationsChange($event)"
                        />
                    </div>
                </div>

                @if (drillDownSelection(); as selection) {
                    <section
                        class="budget-reports-drill-down"
                        report-detail
                        aria-labelledby="budget-reports-drill-down-title"
                    >
                        <header class="budget-reports-drill-down-header">
                            <h2
                                id="budget-reports-drill-down-title"
                                class="budget-reports-drill-down-title"
                                tabindex="-1"
                                #drillDownHeading
                            >
                                {{ selection.widgetTitle }}: {{ selection.rowLabel }}
                            </h2>
                            <button type="button" class="aw-button aw-button--outline" (click)="closeDrillDown()">
                                Close
                            </button>
                        </header>
                        @if (drillDownRes.isLoading()) {
                            <s25-ng-loading-inline-static [text]="'Loading entries...'" />
                        } @else if (drillDownError()) {
                            <p class="budget-reports-error" role="alert">{{ drillDownError() }}</p>
                        } @else if (drillDownTable(); as table) {
                            <s25-ng-table
                                [dataSource]="table"
                                [columnSortable]="true"
                                [hasFilter]="true"
                                [hasTotalRowCount]="true"
                                [hasExport]="true"
                                [exportFilename]="drillDownExportFileName()"
                                [exportSheetName]="selection.widgetTitle"
                                [caption]="selection.widgetTitle + ': ' + selection.rowLabel"
                                [noResultsMessage]="'No entries found'"
                            />
                        }
                    </section>
                }
            </s25-ng-report-dashboard>
        }
    `,
    styles: `
        :host {
            display: block;
            padding: 1.5rem;
            max-width: 1400px;
            margin: 0 auto;
        }
        .budget-reports-title {
            margin: 0 0 1rem;
            font-size: 2rem;
            color: var(--color--text);
        }
        .budget-reports-controls {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            align-items: flex-end;
        }
        .budget-reports-field {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }
        .budget-reports-field label,
        .budget-reports-field-label {
            font-size: 0.875rem;
            font-weight: 600;
            margin: 0;
        }
        .budget-reports-field--wide {
            min-width: 16rem;
        }
        .budget-reports-fiscal-year {
            min-width: 160px;
        }
        .budget-reports-empty {
            color: var(--color--text-muted);
        }
        .budget-reports-error {
            color: var(--color--text-danger);
        }
        .budget-reports-drill-down {
            border: 1px solid var(--color--border);
            border-radius: var(--border-radius);
            background: var(--color--surface-2-bg);
            padding: 1rem;
            margin-bottom: 1rem;
        }
        .budget-reports-drill-down-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.5rem;
        }
        .budget-reports-drill-down-title {
            margin: 0;
            font-size: 1.2rem;
        }
    `,
})
export class BudgetReportsComponent {
    readonly labels = BudgetLabels;
    readonly dashboardId = BUDGET_REPORTS_DASHBOARD_ID;
    readonly widgets = BUDGET_REPORT_WIDGETS;
    readonly measureOptions = BUDGET_REPORT_MEASURES;
    readonly formatAmount = formatBudgetAmount;

    // One-way URL state: read the query string once to seed the controls; never again.
    private readonly seed = readSeed();
    private readonly seededFiscalYearId = parseId(this.seed.get("fy"));

    readonly fiscalYearsRes = s25Resource(() => BudgetService.getFiscalYears());
    readonly hasFiscalYears = computed(() => !!this.fiscalYearsRes.value()?.length);

    readonly fiscalYearItem = linkedSignal<Budget.FiscalYear[] | undefined, Nullable<DropDownItem<number>>>({
        source: () => this.fiscalYearsRes.value(),
        computation: (fiscalYears) => defaultFiscalYearItem(fiscalYears, this.seededFiscalYearId),
    });
    readonly fiscalYearId = computed(() => parseId(this.fiscalYearItem()?.itemId));

    readonly measure = signal<Budget.ReportMeasure>(this.seedMeasure());

    // Committed picker selection, seeded with bare {itemId}s from the URL; the picker fills in names.
    readonly orgsModel = signal<DropDownItem[]>(parseIdList(this.seed.get("orgs")).map((itemId) => ({ itemId })));
    readonly organizationIds = computed(() => idsOf(this.orgsModel()));

    readonly filter = computed<Budget.ReportFilter | undefined>(() => {
        const fiscalYearId = this.fiscalYearId();
        if (fiscalYearId === undefined) return undefined;
        const organizationIds = this.organizationIds();
        return {
            fiscalYearIds: [fiscalYearId],
            dimensions: [],
            ...(organizationIds.length ? { organizationIds } : {}),
        };
    });

    readonly reportRes = s25Resource({
        if: () => !!this.filter(),
        params: () => this.filter(),
        loader: ({ params }) => BudgetService.getReportBreakdowns(params!),
    });

    readonly reportError = computed(() => {
        const bodyError = budgetReportValidationMessage(this.reportRes.value());
        if (bodyError) {
            return bodyError;
        }
        const error = this.reportRes.error();
        return error ? S25Util.errorText(error, "Failed to load budget reports.") : undefined;
    });

    readonly reportHasData = computed(() => !budgetReportValidationMessage(this.reportRes.value()));

    readonly series = computed(() => (this.reportHasData() ? (this.reportRes.value()?.series ?? []) : []));
    readonly breakdowns = computed(() =>
        this.reportHasData() ? (this.reportRes.value()?.breakdowns ?? []).map(withPlanTypeDisplayLabels) : [],
    );
    readonly cards = computed(() => (this.reportHasData() ? budgetSummaryCards(this.reportRes.value()?.summary) : []));

    /** Cleared whenever the filter changes, since the old selection no longer matches the charts. */
    readonly drillDownSelection = linkedSignal<Budget.ReportFilter | undefined, DrillDownSelection | null>({
        source: this.filter,
        computation: () => null,
    });

    readonly drillDownRequest = computed<Budget.ReportDrilldownRequest | undefined>(() => {
        const selection = this.drillDownSelection();
        const filter = this.filter();
        if (!selection || !filter) return undefined;
        return {
            ...filter,
            dimension: selection.dimension,
            key: selection.rowKey,
            ...(selection.seriesKey ? { seriesKey: selection.seriesKey } : {}),
        };
    });

    readonly drillDownRes = s25Resource({
        if: () => !!this.drillDownRequest(),
        params: () => this.drillDownRequest(),
        loader: ({ params }) => BudgetService.getReportDrilldown(params),
    });

    readonly drillDownError = computed(() => {
        const bodyError = budgetReportValidationMessage(this.drillDownRes.value());
        if (bodyError) {
            return bodyError;
        }
        const error = this.drillDownRes.error();
        return error ? S25Util.errorText(error, "Failed to load entries.") : undefined;
    });

    readonly drillDownTable = computed<Table.DataSource | undefined>(() => {
        const response = this.drillDownRes.value();
        if (!response || budgetReportValidationMessage(response)) return undefined;
        const rows = drillDownTableRows(response);
        return {
            type: "unpaginated",
            dataSource: () => Promise.resolve({ rows, totalRows: rows.length }),
            columns: drillDownColumns(response.entityType),
        };
    });

    readonly drillDownExportFileName = computed(() => {
        const selection = this.drillDownSelection();
        return reportExportFileName(
            this.labels.BUDGET_REPORTS,
            selection ? `${selection.widgetTitle} ${selection.rowLabel}` : "",
        );
    });

    readonly drillDownHeading = viewChild<ElementRef<HTMLElement>>("drillDownHeading");

    constructor() {
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, TELEMETRY_SUB_CATEGORY, "Open");

        // Move focus to the drill-down panel when it opens so keyboard and screen reader users land on it.
        effect(() => {
            this.drillDownHeading()?.nativeElement.focus();
        });

        // One-way deep links. The seBudgetReports state declares these params with reloadOnSearch:false,
        // so writing them does not reload the view.
        effect(() => {
            StateService.setSearch({
                fy: this.fiscalYearId(),
                measure: this.measure(),
                orgs: this.organizationIds().length ? this.organizationIds().join(",") : undefined,
            });
        });
    }

    onMeasureChange(value: string): void {
        this.measure.set(isBudgetReportMeasure(value) ? value : DEFAULT_BUDGET_REPORT_MEASURE);
    }

    onOrganizationsChange(items: Nullable<DropDownItem[]>): void {
        this.orgsModel.set(items ?? []);
    }

    onDrillDown(event: ReportDrillDownEvent): void {
        const widget = this.widgets.find((candidate) => candidate.key === event.widgetKey);
        if (!widget?.dimension) return;
        this.drillDownSelection.set({
            widgetTitle: widget.title,
            dimension: widget.dimension,
            rowKey: event.rowKey,
            rowLabel: event.rowLabel,
            seriesKey: event.seriesKey,
        });
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, TELEMETRY_SUB_CATEGORY, "DrillDown");
    }

    onExportWidget(): void {
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, TELEMETRY_SUB_CATEGORY, "ExportWidget");
    }

    closeDrillDown(): void {
        this.drillDownSelection.set(null);
    }

    private seedMeasure(): Budget.ReportMeasure {
        const seeded = this.seed.get("measure");
        return isBudgetReportMeasure(seeded) ? seeded : DEFAULT_BUDGET_REPORT_MEASURE;
    }
}
